import { createContext, useEffect, useMemo, useReducer, type ReactNode } from 'react';
import {
  buildInitialState,
  navigationReducer,
  type NavigationAction,
  type NavigationState,
} from './navigation-state';
import { FRESH_LOGIN_KEY, writePersistedObraId, writePersistedView } from './storage';
import { buildUrl, pathToView, queryToStack, stackToQuery } from './url-bridge';

export interface NavigationContextValue {
  state: NavigationState;
  dispatch: React.Dispatch<NavigationAction>;
}

export const NavigationContext = createContext<NavigationContextValue | null>(null);

interface NavigationProviderProps {
  children: ReactNode;
}

export function NavigationProvider({ children }: NavigationProviderProps) {
  // P2-002 — hydrate the initial view from window.location.pathname so a
  // direct visit (paste / bookmark / F5) lands on the right screen instead
  // of whatever the reducer's default/persisted view is. URL wins because
  // it is the user's most explicit intent. Done synchronously inside the
  // reducer initializer so no flash from the URL-bridge push effect below.
  const [state, dispatch] = useReducer(navigationReducer, undefined, () => {
    const initial = buildInitialState();
    if (typeof window === 'undefined') return initial;
    const urlView = pathToView(window.location.pathname);
    const urlStack = queryToStack(window.location.search);
    return {
      ...initial,
      view: urlView ?? initial.view,
      overlayDrawerStack: urlStack,
    };
  });

  // Fresh-login reset: first render after login wipes navigation to home.
  // The fresh_login sessionStorage flag itself is cleared by ProtectedRoute
  // after its loading animation — we only react to its presence.
  useEffect(() => {
    if (sessionStorage.getItem(FRESH_LOGIN_KEY)) {
      dispatch({ type: 'FRESH_LOGIN_RESET' });
    }
    // Intentionally mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(FRESH_LOGIN_KEY)) return;
    writePersistedView(state.view);
  }, [state.view]);

  useEffect(() => {
    if (sessionStorage.getItem(FRESH_LOGIN_KEY)) return;
    writePersistedObraId(state.selection.obraId);
  }, [state.selection.obraId]);

  // P2-002 — bridge view ↔ window.history.
  // 1) Push when the view OR drawer stack changes so the URL reflects both.
  //    The query encodes the stack as `?drawer=kind:id[:mode],...` so F5,
  //    bookmarks and copy-paste links restore the full overlay state.
  //    External query params (anything other than `drawer`) are preserved so
  //    deep-links from notifications can carry one-shot flags like
  //    `?confirmDelivery=1` to the destination drawer; the drawer is
  //    responsible for clearing the flag once it has consumed it.
  useEffect(() => {
    const baseTarget = buildUrl(state.view, state.overlayDrawerStack);
    const externalParams = new URLSearchParams(window.location.search);
    externalParams.delete('drawer');
    const externalQuery = externalParams.toString();
    const target = externalQuery
      ? `${baseTarget}${baseTarget.includes('?') ? '&' : '?'}${externalQuery}`
      : baseTarget;
    const current = window.location.pathname + window.location.search;
    if (current !== target) {
      window.history.pushState(
        { view: state.view, drawer: stackToQuery(state.overlayDrawerStack) },
        '',
        target,
      );
    }
  }, [state.view, state.overlayDrawerStack]);

  // 2) Listen to popstate (back/forward) and re-derive view + drawer stack
  //    from the URL. Unknown paths leave the view alone so the reducer keeps
  //    its current state instead of falling back to home; an empty/absent
  //    `?drawer=` clears the stack (which is the desired back-button UX:
  //    pressing back from a stacked drawer pops one entry).
  useEffect(() => {
    const handler = () => {
      const view = pathToView(window.location.pathname);
      const stack = queryToStack(window.location.search);
      if (view && view !== state.view) {
        dispatch({ type: 'NAVIGATE', view });
      }
      if (stackToQuery(state.overlayDrawerStack) !== stackToQuery(stack)) {
        dispatch({ type: 'SYNC_OVERLAY_DRAWER_STACK', stack });
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [state.view, state.overlayDrawerStack]);

  const value = useMemo(() => ({ state, dispatch }), [state]);

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}
